import React, { useEffect, useMemo, useState } from "react";
import Classes from "../Information.module.css";
import AddressAutocomplete from "./AddressAutocomplete";
import backend from "../../../../../../api";
import { useSelector } from "react-redux";

const DAY_LABEL = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };
const defaultSchedule = [
  { day: 'mon', enabled: true, windows: [{ start: '09:00', end: '17:00' }] },
  { day: 'tue', enabled: true, windows: [{ start: '09:00', end: '17:00' }] },
  { day: 'wed', enabled: true, windows: [{ start: '09:00', end: '17:00' }] },
  { day: 'thu', enabled: true, windows: [{ start: '09:00', end: '17:00' }] },
  { day: 'fri', enabled: true, windows: [{ start: '09:00', end: '17:00' }] },
  { day: 'sat', enabled: true, windows: [{ start: '09:00', end: '17:00' }] },
  { day: 'sun', enabled: true, windows: [{ start: '09:00', end: '17:00' }] },
];

function fmtDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function getMonthMatrix(anchor) {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const start = new Date(first);
  const offset = first.getDay(); // 0..6 (Sun..Sat)
  start.setDate(first.getDate() - offset);
  const days = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push(d);
  }
  return days;
}
function fmt12(hhmm) {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hh = h % 12 || 12;
  return `${hh}:${String(m).padStart(2, '0')} ${ampm}`;
}
const idxToKey = ['sun','mon','tue','wed','thu','fri','sat'];
const keyToIdx = { sun:0, mon:1, tue:2, wed:3, thu:4, fri:5, sat:6 };
function isoWeekday(iso) { return idxToKey[new Date(iso).getDay()]; }

export default function ServiceTab() {
  const company = useSelector((s) => s.companyInfo.info);
  const companyId = company?._id;

  const [saving, setSaving] = useState(false);
  const [serviceId, setServiceId] = useState(null);

  const [prices, setPrices] = useState({ regular: "", mid: "", premium: "", emergencyFee: "" });
  const [areas, setAreas] = useState([
    { line1: "", city: "", state: "", postalCode: "", country: "", placeId: "", lat: "", lng: "" }
  ]);

  // Weekly schedule (by default all enabled)
  const [schedule, setSchedule] = useState(defaultSchedule);

  // Mini calendar state
  const [calMonth, setCalMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const monthDays = useMemo(() => getMonthMatrix(calMonth), [calMonth]);
  const calLabel = useMemo(() => calMonth.toLocaleString(undefined, { month: 'long', year: 'numeric' }), [calMonth]);

  // Date overrides: Map<YYYY-MM-DD, { enabled: boolean, start: string, end: string }>
  const [overrides, setOverrides] = useState(new Map());

  // Current date being edited in the right-hand controls
  const [currentEditDate, setCurrentEditDate] = useState(null);

  // Editor window reflects overrides[currentEditDate] or weekly default
  const [overrideWindow, setOverrideWindow] = useState({ start: '09:00', end: '17:00', enabled: true });

  const weeklyFor = (weekdayKey) => schedule.find(s => s.day === weekdayKey) || { enabled: false, windows: [{ start: '09:00', end: '17:00' }] };

  // Fetch existing service (by company)
  useEffect(() => {
    if (!companyId) return;
    (async () => {
      try {
        const { data } = await backend.get(`/api/services/by-company/${companyId}`);
        if (data?.ok && data.data) {
          const s = data.data;
          setServiceId(s._id);
          setPrices({
            regular: s.prices?.regular ?? "",
            mid: s.prices?.mid ?? "",
            premium: s.prices?.premium ?? "",
            emergencyFee: s.prices?.emergencyFee ?? "",
          });
          setAreas(
            (s.areas?.length ? s.areas : [{ line1: "", city: "", state: "", postalCode: "", country: "", placeId: "", lat: "", lng: "" }])
              .map(a => ({
                line1: a.line1 || "",
                city: a.city || "",
                state: a.state || "",
                postalCode: a.postalCode || "",
                country: a.country || "",
                placeId: a.placeId || "",
                lat: (a.lat ?? "")?.toString(),
                lng: (a.lng ?? "")?.toString(),
              }))
          );
          setSchedule(
            Array.isArray(s.weeklySchedule) && s.weeklySchedule.length === 7
              ? s.weeklySchedule.map(d => ({
                  day: d.day,
                  enabled: !!d.enabled,
                  windows: (d.windows?.length ? d.windows : [{ start: '09:00', end: '17:00' }])
                }))
              : defaultSchedule
          );
          if (Array.isArray(s.dateOverrides) && s.dateOverrides.length) {
            const map = new Map();
            s.dateOverrides.forEach(o => {
              map.set(o.date, {
                enabled: !!o.enabled,
                start: o.windows?.[0]?.start || '09:00',
                end: o.windows?.[0]?.end || '17:00'
              });
            });
            setOverrides(map);
            if (s.dateOverrides[0]) {
              const o = s.dateOverrides[0];
              setCurrentEditDate(o.date);
              setOverrideWindow({
                enabled: !!o.enabled,
                start: o.windows?.[0]?.start || '09:00',
                end: o.windows?.[0]?.end || '17:00'
              });
            }
          }
        }
      } catch (e) {
        console.error("Fetch service error:", e);
      }
    })();
  }, [companyId]);

  // Areas helpers
  const setArea = (idx, patch) => setAreas(prev => prev.map((a, i) => i === idx ? { ...a, ...patch } : a));
  const addArea = () => setAreas(prev => [...prev, { line1: "", city: "", state: "", postalCode: "", country: "", placeId: "", lat: "", lng: "" }]);
  const removeArea = (idx) => {
    const arr = areas.filter((_, i) => i !== idx);
    setAreas(arr.length ? arr : [{ line1: "", city: "", state: "", postalCode: "", country: "", placeId: "", lat: "", lng: "" }]);
  };

  // Toggle entire weekday on/off
  const toggleDay = (dayKey) => {
    setSchedule(prev => {
      const next = prev.map(d => d.day === dayKey
        ? { ...d, enabled: !d.enabled, windows: d.windows?.length ? d.windows : [{ start: '09:00', end: '17:00' }] }
        : d
      );

      const nowEnabled = !prev.find(d => d.day === dayKey)?.enabled;

      // Cascade to calendar:
      setOverrides(prevMap => {
        const n = new Map(prevMap);
        // Pass 1: enforce disabled for ALL existing overrides that match this weekday
        for (const [dateISO, ov] of n.entries()) {
          if (isoWeekday(dateISO) === dayKey) {
            if (!nowEnabled) {
              // If weekday unchecked, force disabled
              n.set(dateISO, { ...ov, enabled: false });
            } else {
              // Weekday re-enabled: if the override was only disabling without custom hours,
              // drop it so the date inherits weekly hours again
              const w = weeklyFor(dayKey);
              const onlyDisabled = ov.enabled === false;
              const matchesWeeklyTimes = (!ov.start && !ov.end) || (ov.start === w.windows[0]?.start && ov.end === w.windows[0]?.end);
              if (onlyDisabled && matchesWeeklyTimes) n.delete(dateISO);
              else n.set(dateISO, { ...ov, enabled: true }); // keep custom ones but re-enable
            }
          }
        }
        return n;
      });

      return next;
    });
  };

  // Change weekly window (single window per weekday UI)
  const changeWindow = (day, field, value) => {
    setSchedule(prev => prev.map(d => {
      if (d.day !== day) return d;
      const w = d.windows?.[0] || { start: '09:00', end: '17:00' };
      const nw = { ...w, [field]: value };
      return { ...d, windows: [nw] };
    }));
  };

  const isSameMonth = (d) => d.getMonth() === calMonth.getMonth();
  const isToday = (d) => {
    const t = new Date();
    return d.getFullYear() === t.getFullYear() && d.getMonth() === t.getMonth() && d.getDate() === t.getDate();
  };

  // Ensure the side editor always reflects the latest source of truth
  useEffect(() => {
    if (!currentEditDate) return;
    const ov = overrides.get(currentEditDate);
    if (ov) {
      setOverrideWindow(ov);
    } else {
      const wd = weeklyFor(isoWeekday(currentEditDate));
      setOverrideWindow({
        enabled: !!wd.enabled,
        start: wd.windows?.[0]?.start || '09:00',
        end: wd.windows?.[0]?.end || '17:00',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEditDate, overrides, schedule]);

  // Clicking a date:
  // - If no override exists, create one inheriting weekly defaults (enabled)
  // - If override exists, toggle enabled (disable/enable) with single click
  // - Always set it as current edit date so times can be changed immediately
  const handleDateClick = (d) => {
    const key = fmtDate(d);
    const wd = weeklyFor(idxToKey[d.getDay()]);
    setOverrides(prev => {
      const n = new Map(prev);
      const existing = n.get(key);
      if (!existing) {
        // Create fresh override from weekly defaults (editable by default)
        n.set(key, {
          enabled: !!wd.enabled,
          start: wd.windows?.[0]?.start || '09:00',
          end: wd.windows?.[0]?.end || '17:00'
        });
      } else {
        // Toggle enabled
        n.set(key, { ...existing, enabled: !existing.enabled });
      }
      return n;
    });
    setCurrentEditDate(key);
  };

  // Clear a single override → go back to weekly
  const removeCurrentOverride = () => {
    if (!currentEditDate) return;
    setOverrides(prev => {
      const n = new Map(prev);
      n.delete(currentEditDate);
      return n;
    });
    setCurrentEditDate(null);
    setOverrideWindow({ start: '09:00', end: '17:00', enabled: true });
  };

  // Clear all overrides
  const clearOverrides = () => {
    setOverrides(new Map());
    setCurrentEditDate(null);
    setOverrideWindow({ start: '09:00', end: '17:00', enabled: true });
  };

  // Change the time for the current override (auto-creates override if editing weekly-inherited day)
  const handleTimeChange = (field, value) => {
    if (!currentEditDate) return;
    setOverrides(prev => {
      const n = new Map(prev);
      const existing = n.get(currentEditDate);
      if (existing) {
        n.set(currentEditDate, { ...existing, [field]: value });
      } else {
        const wd = weeklyFor(isoWeekday(currentEditDate));
        n.set(currentEditDate, {
          enabled: !!wd.enabled,
          start: field === 'start' ? value : (wd.windows?.[0]?.start || '09:00'),
          end: field === 'end' ? value : (wd.windows?.[0]?.end || '17:00'),
        });
      }
      return n;
    });
  };

  // Enable/Disable current date via button
  const toggleCurrentDate = () => {
    if (!currentEditDate) return;
    setOverrides(prev => {
      const n = new Map(prev);
      const ov = n.get(currentEditDate);
      if (ov) {
        n.set(currentEditDate, { ...ov, enabled: !ov.enabled });
      } else {
        const wd = weeklyFor(isoWeekday(currentEditDate));
        n.set(currentEditDate, { enabled: !wd.enabled, start: wd.windows?.[0]?.start || '09:00', end: wd.windows?.[0]?.end || '17:00' });
      }
      return n;
    });
  };

  // Build payload
  const payload = useMemo(() => {
    const dateOverrides = Array.from(overrides).map(([date, o]) => ({
      date,
      enabled: o.enabled,
      windows: o.enabled ? [{ start: o.start, end: o.end }] : [],
    }));
    return {
      companyId,
      prices: {
        regular: Number(prices.regular) || 0,
        mid: Number(prices.mid) || 0,
        premium: Number(prices.premium) || 0,
        emergencyFee: Number(prices.emergencyFee) || 0,
      },
      areas: areas.map(a => ({
        line1: a.line1,
        city: a.city,
        state: a.state,
        postalCode: a.postalCode,
        country: a.country,
        placeId: a.placeId,
        lat: a.lat !== "" && Number.isFinite(Number(a.lat)) ? Number(a.lat) : null,
        lng: a.lng !== "" && Number.isFinite(Number(a.lng)) ? Number(a.lng) : null,
      })),
      weeklySchedule: schedule,
      dateOverrides,
    };
  }, [companyId, prices, areas, schedule, overrides]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!companyId) {
      alert("Missing companyId. Save company info first.");
      return;
    }
    try {
      setSaving(true);
      if (serviceId) {
        const { data } = await backend.put(`/api/services/${serviceId}`, payload);
        if (data?.ok) alert("Service updated");
      } else {
        const { data } = await backend.post(`/api/services`, payload);
        if (data?.ok) {
          setServiceId(data.data?._id);
          alert("Service saved");
        }
      }
    } catch (err) {
      console.error("Save service error:", err);
      alert(err?.response?.data?.error || "Server error");
    } finally {
      setSaving(false);
    }
  };

  // Inline styles for the mini calendar (so we don't change your CSS files)
  const calStyles = {
    card: { border: '1px solid #dedede', borderRadius: 10, padding: 12, background: '#fff' },
    headerRow: { display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: 8, gap: 15, flexWrap: 'wrap' },
    navBtn: { background: '#ffffff', border: '1px solid rgba(0,0,0,0.1)', borderRadius: 7, padding: '6px 10px', cursor: 'pointer', fontSize: 12 },
    dowRow: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6, marginBottom: 6, fontSize: 12, color: '#6b7280', textAlign: 'center' },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6 },
    cell: { border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 0', textAlign: 'center', cursor: 'pointer', userSelect: 'none', fontSize: 12, background: '#fff' },
    cellMuted: { color: '#9ca3af', background: '#fafafa' },
    cellToday: { outline: '2px solid var(--secondary-color)', outlineOffset: -2 },
    cellSelected: { background: 'rgba(23,43,67,0.1)', borderColor: 'rgba(23,43,67,0.2)', fontWeight: 700 },
    cellDisabled: { background: 'rgba(255,0,0,0.08)', borderColor: 'rgba(255,0,0,0.2)' },
    legend: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, gap: 8, flexWrap: 'wrap' },
    timeRow: { display: 'grid', gridTemplateColumns: 'auto 1fr 1fr', gap: 10, marginTop: 10 },
    smallHint: { fontSize: 12, color: '#6b7280' }
  };

  return (
    <form className={Classes.form} onSubmit={onSubmit}>
      {/* PRICES */}
      <div className={Classes.flexColGroup}>
        <div className={Classes.flexCol1}>
          <h5 className={Classes.label}>Regular (87) — $/gallon</h5>
          <input type="number" min="0" step="0.01" className={Classes.inputField}
            value={prices.regular} onChange={e => setPrices(p => ({ ...p, regular: e.target.value }))} placeholder="4.50" required />
        </div>
        <div className={Classes.flexCol1}>
          <h5 className={Classes.label}>Diesel  — $/gallon</h5>
          <input type="number" min="0" step="0.01" className={Classes.inputField}
            value={prices.mid} onChange={e => setPrices(p => ({ ...p, mid: e.target.value }))} placeholder="5.00" required />
        </div>
      </div>

      <div className={Classes.flexColGroup}>
        <div className={Classes.flexCol1}>
          <h5 className={Classes.label}>Premium (91–93) — $/gallon</h5>
          <input type="number" min="0" step="0.01" className={Classes.inputField}
            value={prices.premium} onChange={e => setPrices(p => ({ ...p, premium: e.target.value }))} placeholder="5.50" required />
        </div>
        <div className={Classes.flexCol1}>
          <div className={Classes.flexAlign10}>
            <h5 className={Classes.label}>Emergency Fee</h5>
            <div className={Classes.optionalText}><h5>Optional</h5></div>
          </div>
          <input type="number" min="0" step="0.01" className={Classes.inputField}
            value={prices.emergencyFee} onChange={e => setPrices(p => ({ ...p, emergencyFee: e.target.value }))} placeholder="5.00" />
        </div>
      </div>

      {/* AREAS */}
      <div className={Classes.flexColGroup}>
        <div className={Classes.flexCol1}>
          <h4 className={Classes.label}>Service Areas (custom or auto address)</h4>
          {areas.map((a, idx) => (
            <div key={idx} style={{ border: '1px solid #dedede', borderRadius: 10, padding: 10, marginBottom: 10, background: '#fff' }}>
              <AddressAutocomplete
                value={a.line1}
                onChange={(text) => setArea(idx, { line1: text })}
                onSelect={(res) => setArea(idx, {
                  line1: res.formatted || a.line1,
                  city: res.components?.city || "",
                  state: res.components?.state || "",
                  postalCode: res.components?.postalCode || "",
                  country: res.components?.country || "",
                  placeId: res.placeId || "",
                  lat: res.lat ?? "",
                  lng: res.lng ?? ""
                })}
              />
              <div className={Classes.addrMetaRow}>
                <input className={Classes.inputField} placeholder="City" value={a.city} onChange={e => setArea(idx, { city: e.target.value })} />
                <input className={Classes.inputField} placeholder="State/Province" value={a.state} onChange={e => setArea(idx, { state: e.target.value })} />
              </div>
              <div className={Classes.addrMetaRow}>
                <input className={Classes.inputField} placeholder="Postal Code" value={a.postalCode} onChange={e => setArea(idx, { postalCode: e.target.value })} />
                <input className={Classes.inputField} placeholder="Country" value={a.country} onChange={e => setArea(idx, { country: e.target.value })} />
              </div>
              <div className={Classes.navRow}>
                <button type="button" className={Classes.saveBtn} onClick={() => removeArea(idx)}>
                  <h5>Remove Area</h5>
                </button>
              </div>
            </div>
          ))}
          <button type="button" className={Classes.saveBtn} onClick={addArea}><h5>+ Add Area</h5></button>
        </div>
      </div>

      {/* WEEKLY AVAILABILITY */}
      <div className={Classes.flexColGroup}>
        <div className={Classes.flexCol1}>
          <h3>Availibility</h3>
          <h4 className={Classes.label}>Weekly Availability</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 10 }}>
            {schedule.map((d) => (
              <div key={d.day} style={{ border: '1px solid #dedede', borderRadius: 10, padding: 10, background: '#fff' }}>
                <label className={Classes.label} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input
                    type="checkbox"
                    checked={d.enabled}
                    onChange={() => toggleDay(d.day)}
                  /> {DAY_LABEL[d.day]}
                </label>
                {d.enabled && (
                  <div className={Classes.addrMetaRow} style={{ marginTop: 10 }}>
                    <div>
                      <div className={Classes.label}>From</div>
                      <input
                        type="time"
                        className={Classes.inputField}
                        value={d.windows[0]?.start || '09:00'}
                        onChange={e => changeWindow(d.day, 'start', e.target.value)}
                      />
                      <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>{fmt12(d.windows[0]?.start || '09:00')}</div>
                    </div>
                    <div>
                      <div className={Classes.label}>To</div>
                      <input
                        type="time"
                        className={Classes.inputField}
                        value={d.windows[0]?.end || '17:00'}
                        onChange={e => changeWindow(d.day, 'end', e.target.value)}
                      />
                      <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>{fmt12(d.windows[0]?.end || '17:00')}</div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* MINI CALENDAR FOR SPECIFIC DATES */}
      <div className={Classes.flexColGroup}>
        <div className={Classes.flexCol1}>
          <h4 className={Classes.label}>Specific Dates (Overrides)</h4>

          <div style={calStyles.card}>
            <div style={calStyles.headerRow}>
              <button type="button" style={calStyles.navBtn}
                onClick={() => setCalMonth(m => new Date(m.getFullYear(), m.getMonth() - 1, 1))}>‹ Prev</button>
              <div className={Classes.title3} style={{ fontSize: 16 }}>{calLabel}</div>
              <button type="button" style={calStyles.navBtn}
                onClick={() => setCalMonth(m => new Date(m.getFullYear(), m.getMonth() + 1, 1))}>Next ›</button>
            </div>

            <div style={calStyles.dowRow}>
              <div>Sun</div><div>Mon</div><div>Tue</div><div>Wed</div><div>Thu</div><div>Fri</div><div>Sat</div>
            </div>

            <div style={calStyles.grid}>
              {monthDays.map((d, i) => {
                const key = fmtDate(d);
                const inMonth = isSameMonth(d);
                const today = isToday(d);

                // Effective state:
                const ov = overrides.get(key);
                const wd = weeklyFor(idxToKey[d.getDay()]);
                const effectiveEnabled = ov ? ov.enabled : !!wd.enabled;

                const style = {
                  ...calStyles.cell,
                  ...(inMonth ? {} : calStyles.cellMuted),
                  ...(today ? calStyles.cellToday : {}),
                  ...(effectiveEnabled ? calStyles.cellSelected : calStyles.cellDisabled),
                };

                return (
                  <div
                    key={key + i}
                    style={style}
                    onClick={() => handleDateClick(d)}
                    title={effectiveEnabled
                      ? `Enabled (${(ov?.start || wd.windows?.[0]?.start || '09:00')} - ${(ov?.end || wd.windows?.[0]?.end || '17:00')})`
                      : 'Disabled (click to enable)'}
                  >
                    {d.getDate()}
                  </div>
                );
              })}
            </div>

            <div style={calStyles.legend}>
              <div className={Classes.label} style={calStyles.smallHint}>
                Click a date to toggle enabled/disabled. Times default from the Weekly Availability, but you can customize per date below.
              </div>
              <button type="button" style={calStyles.navBtn} onClick={clearOverrides}>Clear All</button>
              {currentEditDate && (
                <button type="button" style={calStyles.navBtn} onClick={removeCurrentOverride}>Remove Customization</button>
              )}
            </div>

            {currentEditDate && (
              <>
                <div style={{ ...calStyles.smallHint, marginTop: 6 }}>
                  Editing {currentEditDate} — {overrideWindow.enabled ? 'Enabled' : 'Disabled'}
                </div>
                <div style={calStyles.timeRow}>
                  <div>
                    <div className={Classes.label}>Day Status</div>
                    <button type="button" style={calStyles.navBtn} onClick={toggleCurrentDate}>
                      {overrideWindow.enabled ? 'Disable this day' : 'Enable this day'}
                    </button>
                  </div>
                  <div>
                    <div className={Classes.label}>From</div>
                    <input
                      type="time"
                      className={Classes.inputField}
                      value={overrideWindow.start}
                      onChange={(e) => handleTimeChange('start', e.target.value)}
                      disabled={!overrideWindow.enabled}
                    />
                    <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>{fmt12(overrideWindow.start)}</div>
                  </div>
                  <div>
                    <div className={Classes.label}>To</div>
                    <input
                      type="time"
                      className={Classes.inputField}
                      value={overrideWindow.end}
                      onChange={(e) => handleTimeChange('end', e.target.value)}
                      disabled={!overrideWindow.enabled}
                    />
                    <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>{fmt12(overrideWindow.end)}</div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ACTIONS */}
      <div className={Classes.actions}>
        <button type="submit" className={Classes.saveBtn} disabled={saving}>
          <h5>{saving ? "Saving..." : "Save Service"}</h5>
        </button>
      </div>
    </form>
  );
}
